import { useLocation, Link, useNavigate } from "react-router-dom";
import { NavLink } from "@/components/NavLink";
import {
  CommandPalette,
  recordToolVisit,
  useCommandPaletteHotkey,
} from "@/components/CommandPalette";
import { SidebarNav } from "@/components/Sidebar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Settings, Menu, LogOut, Mail, MailOpen, Zap, Search, LayoutDashboard, HeartPulse, Users, Handshake, Newspaper, ShieldAlert, Network, MessagesSquare, Scale, KeyRound, MonitorPlay, Radio } from "lucide-react";
import { SpotlightNotificationBell } from "@/components/SpotlightNotificationBell";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { AskClaraLiveField } from "@/components/AskClaraLiveField";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useNetworkUnread } from "@/hooks/useNetworkChat";
import { useIsPlatformOwner } from "@/hooks/useIsPlatformOwner";
import { useUserProfile } from "@/hooks/useUserProfile";

function MobileNav() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth", { replace: true });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden h-8 w-8">
          <Menu className="h-4 w-4" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 p-0">
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto">
            {/* Reuse the same sidebar JSX so mobile and desktop stay
                in lock-step when tools are added to the registry. */}
            <SidebarNav onNavigate={() => setOpen(false)} />
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:bg-accent hover:text-foreground border-t border-border text-left"
          >
            <LogOut className="h-3.5 w-3.5" />
            Log Out
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function UserDisplay() {
  const navigate = useNavigate();
  // Read from the shared auth context — no per-component getUser call,
  // no extra auth-lock acquisition. The provider already has the email.
  const { session } = useUserProfile();
  const userEmail = session?.user.email ?? null;

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth", { replace: true });
  };

  return (
    <div className="hidden md:flex items-center gap-2">
      {userEmail && (
        <span className="text-xs text-muted-foreground truncate max-w-[140px]">{userEmail}</span>
      )}
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleLogout} title="Log out">
        <LogOut className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export function TopNav() {
  const { pathname } = useLocation();
  const { isAdmin } = useIsAdmin();
  const { isOwner } = useIsPlatformOwner();
  // Phase 5b — in-app unread badge on the network desk link (dark build: admin only).
  const networkUnread = useNetworkUnread(isAdmin);

  // Command palette state. ⌘K / Ctrl+K toggles open from anywhere.
  const [paletteOpen, setPaletteOpen] = useState(false);
  useCommandPaletteHotkey(setPaletteOpen);

  // Track every route change as a "recent visit" so the palette's
  // Recent section reflects where the user has been.
  useEffect(() => {
    recordToolVisit(pathname);
  }, [pathname]);

  return (
    <div className="sticky top-0 z-30">
      <div className="bg-card border-b border-border shadow-sm">
        <div className="flex items-center gap-3 h-12 px-4">
          <MobileNav />
          {/* Logo links to the authenticated home (My Workspace at
              `/dashboard`). `/` is the free public landing, and `/live`
              is now listed as a regular tool in the sidebar alongside
              National Steer Tracker and Clara Live Tracker — no longer
              the authoritative "home" of the authenticated app. */}
          <Link to="/dashboard" className="flex items-center gap-2 shrink-0" title="RaisedBeef — My Workspace">
            <div className="w-7 h-7 rounded bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xs font-serif">RB</span>
            </div>
            <span
              className="text-sm font-semibold text-foreground tracking-tight hidden lg:block"
              style={{ fontFamily: "'Libre Caslon Text', serif" }}
            >
              RaisedBeef
            </span>
          </Link>
          {/* My Workspace is the authed home pill. `/live` is NOT pilled
              here — it's a sidebar tool like Steer Tracker, not a home
              surface. Removing its pill (was "Live Market Dashboard")
              collapses the TopNav to a single home entry. */}
          <NavLink
            to="/dashboard"
            end
            className={cn(
              "hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap shrink-0",
              pathname === "/dashboard"
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            )}
            title="My Workspace — your customizable widget dashboard"
          >
            <LayoutDashboard className="h-3.5 w-3.5" />
            My Workspace
          </NavLink>
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="hidden md:inline-flex items-center gap-2 h-8 pl-2.5 pr-2 rounded-md border border-border bg-background text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors min-w-[200px] max-w-[320px] flex-1"
            title="Search tools, barns, states, regions (Ctrl+K / ⌘K)"
          >
            <Search className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Search tools &amp; markets…</span>
            <kbd className="ml-auto hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono text-muted-foreground shrink-0">
              ⌘K
            </kbd>
          </button>
          {/* Ask Clara Live — colored AI-chat entry point next to the
              ⌘K palette. Premium-gated (matches ConsultantBubble). Type
              a question + Enter to seed the consultant with it and open
              the drawer. */}
          <AskClaraLiveField />
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {/* Mobile-only search icon — desktop uses the ⌘K bar above. */}
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="md:hidden text-muted-foreground hover:text-foreground transition-colors"
              title="Search tools & markets"
              aria-label="Search tools and markets"
            >
              <Search className="h-4 w-4" />
            </button>
            <ThemeSwitcher />
            <SpotlightNotificationBell />
            {/* Platform-owner-only data health dashboard. Gated stricter
                than `isAdmin` because this surface exposes operational
                metadata (DB size, slow queries, error history) that
                shouldn't be visible to ops-team admins — only the
                platform owner. See `useIsPlatformOwner.ts`. */}
            {isOwner && (
              <NavLink
                to="/admin/data-health"
                className="text-muted-foreground hover:text-foreground transition-colors"
                activeClassName="text-primary"
                title="Data Health — platform pulse"
              >
                <HeartPulse className="h-4 w-4" />
              </NavLink>
            )}
            {isAdmin && (
              <>
                <NavLink
                  to="/admin/spotlights"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="Market Spotlights"
                >
                  <Zap className="h-4 w-4" />
                </NavLink>
                <NavLink
                  to="/admin/cmc-live"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="CMC Live — post a video, release held stories"
                >
                  <Radio className="h-4 w-4" />
                </NavLink>
                <NavLink
                  to="/admin/users"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="User management — tier overrides"
                >
                  <Users className="h-4 w-4" />
                </NavLink>
                <NavLink
                  to="/admin/consultant-review"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="Consultant review queue — confident-wrongness flags"
                >
                  <ShieldAlert className="h-4 w-4" />
                </NavLink>
                <NavLink
                  to="/admin/network"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="RaisedBeef Network — member allowlist (dark build)"
                >
                  <Network className="h-4 w-4" />
                </NavLink>
                <NavLink
                  to="/network"
                  className="relative text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="RaisedBeef Network — messages desk (dark build)"
                >
                  <MessagesSquare className="h-4 w-4" />
                  {networkUnread > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 h-3.5 min-w-3.5 px-0.5 rounded-full bg-primary text-primary-foreground text-[9px] leading-[14px] text-center font-medium">
                      {networkUnread > 9 ? "9+" : networkUnread}
                    </span>
                  )}
                </NavLink>
                <NavLink
                  to="/admin/network-compliance"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="Network compliance — trade surveillance (dark build)"
                >
                  <Scale className="h-4 w-4" />
                </NavLink>
                <NavLink
                  to="/admin/barns"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="Barn logins — provision a sale barn's /my-barn dashboard login"
                >
                  <KeyRound className="h-4 w-4" />
                </NavLink>
                <NavLink
                  to="/admin/barn-sponsors"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="Barn sponsors — manage per-barn sponsorship slots"
                >
                  <Handshake className="h-4 w-4" />
                </NavLink>
                <NavLink
                  to="/admin/board-ads"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="Board ads — banner inventory for the in-lobby smart board (RaisedBeef-sold)"
                >
                  <MonitorPlay className="h-4 w-4" />
                </NavLink>
                <NavLink
                  to="/admin/barn-subscriptions"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="Barn sale subscribers — roster + CSV export"
                >
                  <MailOpen className="h-4 w-4" />
                </NavLink>
                <NavLink
                  to="/email-dashboard"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="Email Monitor"
                >
                  <Mail className="h-4 w-4" />
                </NavLink>
                <NavLink
                  to="/admin/reports/cattle-on-feed"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  activeClassName="text-primary"
                  title="Cattle on Feed Reports — draft, edit, publish"
                >
                  <Newspaper className="h-4 w-4" />
                </NavLink>
              </>
            )}
            <NavLink
              to="/settings"
              className="text-muted-foreground hover:text-foreground transition-colors"
              activeClassName="text-primary"
            >
              <Settings className="h-4 w-4" />
            </NavLink>
            <UserDisplay />
          </div>
        </div>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
